import type {
  AuthLoginResponse,
  AuthSessionResponse,
  PasskeyLoginStartResponse,
  PnlResponse,
  SystemStateResponse,
} from "./types";

function getCookieValue(name: string): string {
  if (typeof document === "undefined") return "";
  const needle = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const p = part.trim();
    if (p.startsWith(needle)) return decodeURIComponent(p.slice(needle.length));
  }
  return "";
}

export class ApiHttpError extends Error {
  status: number;
  body: string;
  url: string;

  constructor(url: string, status: number, body: string) {
    const trimmed = body.trim();
    const suffix = trimmed ? `: ${trimmed.slice(0, 240)}` : "";
    super(`${url} failed (${status})${suffix}`);
    this.name = "ApiHttpError";
    this.status = status;
    this.body = body;
    this.url = url;
  }
}

async function fetchJson<T>(url: string, signal?: AbortSignal, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { signal, ...init });
  if (!res.ok) {
    let body = "";
    try {
      body = (await res.text()) || "";
    } catch {
      body = "";
    }
    throw new ApiHttpError(url, res.status, body);
  }
  return (await res.json()) as T;
}

// ---- Data views ----

export async function fetchSystemStateV2(signal?: AbortSignal): Promise<SystemStateResponse> {
  return fetchJson<SystemStateResponse>("/api/v2/system-state", signal);
}

export async function fetchPnlV2(signal?: AbortSignal): Promise<PnlResponse> {
  return fetchJson<PnlResponse>("/api/v2/pnl", signal);
}

// ---- Auth (reused as-is; the session cookie rides same-origin, writes attach the CSRF token) ----

export async function fetchAuthSession(signal?: AbortSignal): Promise<AuthSessionResponse> {
  return fetchJson<AuthSessionResponse>("/api/auth/session", signal);
}

export async function loginPassword(
  payload: { username: string; password: string },
  signal?: AbortSignal
): Promise<AuthLoginResponse> {
  return fetchJson<AuthLoginResponse>("/api/auth/login", signal, {
    method: "POST",
    headers: {
      "content-type": "application/json"
    },
    body: JSON.stringify(payload)
  });
}

export async function logoutSession(signal?: AbortSignal): Promise<{ ok: boolean }> {
  const csrf = getCookieValue("__Host-divina_csrf");
  return fetchJson<{ ok: boolean }>("/api/auth/logout", signal, {
    method: "POST",
    headers: csrf
      ? {
          "x-csrf-token": csrf
        }
      : undefined
  });
}

export async function refreshAuthSession(signal?: AbortSignal): Promise<AuthSessionResponse> {
  const csrf = getCookieValue("__Host-divina_csrf");
  return fetchJson<AuthSessionResponse>("/api/auth/session/refresh", signal, {
    method: "POST",
    headers: csrf
      ? {
          "x-csrf-token": csrf
        }
      : undefined
  });
}

export async function verifyTotpChallenge(
  payload: { challenge_id: string; code: string },
  signal?: AbortSignal
): Promise<AuthLoginResponse> {
  return fetchJson<AuthLoginResponse>("/api/auth/mfa/totp/challenge/verify", signal, {
    method: "POST",
    headers: {
      "content-type": "application/json"
    },
    body: JSON.stringify(payload)
  });
}

export async function verifyRecoveryCodeChallenge(
  payload: { challenge_id: string; code: string },
  signal?: AbortSignal
): Promise<AuthLoginResponse> {
  return fetchJson<AuthLoginResponse>("/api/auth/mfa/recovery/verify", signal, {
    method: "POST",
    headers: {
      "content-type": "application/json"
    },
    body: JSON.stringify(payload)
  });
}

export async function startPasskeyLogin(
  payload: { username?: string },
  signal?: AbortSignal
): Promise<PasskeyLoginStartResponse> {
  return fetchJson<PasskeyLoginStartResponse>("/api/auth/passkey/login/start", signal, {
    method: "POST",
    headers: {
      "content-type": "application/json"
    },
    body: JSON.stringify(payload)
  });
}

export async function finishPasskeyLogin(
  payload: {
    challenge_id: string;
    challenge: string;
    origin: string;
    rp_id: string;
    credential_id: string;
    sign_count: number;
  },
  signal?: AbortSignal
): Promise<AuthLoginResponse> {
  return fetchJson<AuthLoginResponse>("/api/auth/passkey/login/finish", signal, {
    method: "POST",
    headers: {
      "content-type": "application/json"
    },
    body: JSON.stringify(payload)
  });
}
